"use client"

import * as React from "react"
import { type Control, type FieldPath, type FieldValues } from "react-hook-form"

import { useFormBranchContext } from "@/features/auth"
import { useAuthContextStore } from "@/features/auth/services/auth-context-store"
import { useMembersStore } from "@/features/members/services/members-store"
import { useLookup } from "@/shared/hooks/use-lookup"
import { resolveMemberLookupScope } from "@/shared/lib/branch/member-lookup-scope"
import { LookupType, type LookupItem } from "@/shared/model/lookup-types"
import { TenantScopeContext } from "@/shared/model/tenant-scope-context"
import { FormControlledField, LookupSelect, type SelectOption } from "@ibada/ui"

export type AsyncLookupOption = Omit<SelectOption, "label"> & {
  label: string
  metadata?: LookupItem["metadata"]
}

type UseMemberLookupOptionsParams = {
  targetBranchId?: string | null
  forceParishContext?: boolean
  query?: string
  enabled?: boolean
  limit?: number
  fallbackOptions?: AsyncLookupOption[]
  staticOptions?: AsyncLookupOption[]
  selectedValue?: string
  selectedLabel?: string
  /** Member ids to hide from the options (e.g. members already assigned to the group/ministry). */
  excludeIds?: string[]
}

function mergeOptions(...groups: AsyncLookupOption[][]) {
  const byValue = new Map<string, AsyncLookupOption>()

  for (const group of groups) {
    for (const option of group) {
      if (!option.value.trim()) continue
      if (!byValue.has(option.value)) {
        byValue.set(option.value, option)
      }
    }
  }

  return Array.from(byValue.values())
}

function filterOptionsByQuery(options: AsyncLookupOption[], query: string) {
  const normalizedQuery = query.trim().toLocaleLowerCase()
  if (!normalizedQuery) return options

  return options.filter((option) =>
    option.label.toLocaleLowerCase().includes(normalizedQuery),
  )
}

export function useMemberLookupOptions(params: UseMemberLookupOptionsParams) {
  const workspaceId = useAuthContextStore((state) => state.activeWorkspaceId)
  const { accessibleBranches, activeBranchId } = useFormBranchContext()
  const members = useMembersStore((state) => state.members)
  const resolvedScope = React.useMemo(
    () =>
      resolveMemberLookupScope({
        accessibleBranches,
        activeBranchId,
        targetBranchId: params.targetBranchId,
        forceParishContext: params.forceParishContext,
      }),
    [accessibleBranches, activeBranchId, params.forceParishContext, params.targetBranchId],
  )

  const lookup = useLookup({
    type: LookupType.Members,
    workspaceId: workspaceId ?? undefined,
    branchId: resolvedScope.branchId ?? undefined,
    context: resolvedScope.parishContext ? TenantScopeContext.Parish : TenantScopeContext.Branch,
    query: params.query,
    limit: params.limit ?? 10,
    enabled:
      (params.enabled ?? true) &&
      Boolean(workspaceId) &&
      Boolean(resolvedScope.branchId),
  })

  const lookupOptions = React.useMemo<AsyncLookupOption[]>(
    () => (lookup.data?.items ?? []).map((item) => ({
      value: item.id,
      label: item.label,
      metadata: item.metadata,
    })),
    [lookup.data?.items],
  )
  const selectedMember = React.useMemo(
    () =>
      params.selectedValue
        ? members.find((member) => member.id === params.selectedValue) ?? null
        : null,
    [members, params.selectedValue],
  )

  // Resolve the selected value's display label from the best available source: an explicit label, the
  // members store, or the loaded lookup options. We cache the last good label so it survives query/page
  // changes (after selecting, the search resets and the picked member may leave the loaded page). Without
  // this the control would fall back to rendering the raw id, which reads as an "encrypted" name.
  const selectedLabelCacheRef = React.useRef<Record<string, string>>({})
  const resolvedSelectedLabel = params.selectedValue
    ? params.selectedLabel?.trim() ||
      selectedMember?.name?.trim() ||
      lookupOptions.find((option) => option.value === params.selectedValue)?.label?.trim() ||
      selectedLabelCacheRef.current[params.selectedValue]
    : undefined
  React.useEffect(() => {
    if (params.selectedValue && resolvedSelectedLabel && resolvedSelectedLabel !== params.selectedValue) {
      selectedLabelCacheRef.current[params.selectedValue] = resolvedSelectedLabel
    }
  }, [params.selectedValue, resolvedSelectedLabel])

  const selectedOption = React.useMemo<AsyncLookupOption[]>(
    () =>
      params.selectedValue
        ? [{ value: params.selectedValue, label: resolvedSelectedLabel || params.selectedValue }]
        : [],
    [params.selectedValue, resolvedSelectedLabel],
  )
  const normalizedQuery = params.query?.trim() ?? ""
  const filteredStaticOptions = React.useMemo(
    () => filterOptionsByQuery(params.staticOptions ?? [], normalizedQuery),
    [normalizedQuery, params.staticOptions],
  )
  const filteredFallbackOptions = React.useMemo(
    () => filterOptionsByQuery(params.fallbackOptions ?? [], normalizedQuery),
    [normalizedQuery, params.fallbackOptions],
  )
  const options = React.useMemo(
    () => {
      if (!normalizedQuery) {
        return mergeOptions(
          params.staticOptions ?? [],
          selectedOption,
          params.fallbackOptions ?? [],
          lookupOptions,
        )
      }

      return mergeOptions(
        filteredStaticOptions,
        selectedOption,
        filteredFallbackOptions,
        lookupOptions,
      )
    },
    [
      filteredFallbackOptions,
      filteredStaticOptions,
      lookupOptions,
      normalizedQuery,
      selectedOption,
    ],
  )

  const excludeSet = React.useMemo(
    () => new Set((params.excludeIds ?? []).filter(Boolean)),
    [params.excludeIds],
  )
  const visibleOptions = React.useMemo(
    () => (excludeSet.size === 0 ? options : options.filter((option) => !excludeSet.has(option.value))),
    [excludeSet, options],
  )

  return {
    ...lookup,
    options: visibleOptions,
    scope: resolvedScope,
  }
}

type MemberAsyncSelectProps = {
  value?: string
  onValueChange: (value: string) => void
  targetBranchId?: string | null
  forceParishContext?: boolean
  placeholder?: string
  disabled?: boolean
  id?: string
  className?: string
  controlClassName?: string
  menuClassName?: string
  clearable?: boolean
  searchPlaceholder?: string
  loadingMessage?: React.ReactNode
  noOptionsMessage?: React.ReactNode
  mobileEmpty?: React.ReactNode
  mobileTitle?: React.ReactNode
  mobileDescription?: React.ReactNode
  mobileFooter?: React.ReactNode
  mobileCloseLabel?: string
  fallbackOptions?: AsyncLookupOption[]
  staticOptions?: AsyncLookupOption[]
  selectedLabel?: string
  /** Member ids to hide from the options (e.g. members already assigned). */
  excludeIds?: string[]
}

export function MemberAsyncSelect({
  value,
  onValueChange,
  targetBranchId,
  forceParishContext = false,
  placeholder = "Search members",
  disabled = false,
  id,
  className,
  controlClassName,
  menuClassName,
  clearable = false,
  searchPlaceholder = "Type to search",
  loadingMessage = "Loading members...",
  noOptionsMessage,
  mobileEmpty,
  mobileTitle,
  mobileDescription,
  mobileFooter,
  mobileCloseLabel,
  fallbackOptions,
  staticOptions,
  selectedLabel,
  excludeIds,
}: MemberAsyncSelectProps) {
  const [query, setQuery] = React.useState("")
  const { options, isLoading, isError } = useMemberLookupOptions({
    targetBranchId,
    forceParishContext,
    query,
    enabled: !disabled,
    fallbackOptions,
    staticOptions,
    selectedValue: value,
    selectedLabel,
    excludeIds,
  })

  const resolvedValue = value && value.trim().length > 0 ? value : undefined

  return (
    <LookupSelect
      value={resolvedValue}
      onValueChange={onValueChange}
      disabled={disabled}
      id={id}
      options={options}
      isClearable={clearable}
      searchValue={query}
      onSearchValueChange={setQuery}
      placeholder={placeholder}
      searchPlaceholder={searchPlaceholder}
      isLoading={isLoading}
      loadingMessage={loadingMessage}
      noOptionsMessage={noOptionsMessage ?? (isError ? "Unable to load members" : "No members found")}
      mobileEmpty={mobileEmpty ?? (isError ? "Unable to load members" : "No members found")}
      mobileTitle={mobileTitle}
      mobileDescription={mobileDescription}
      mobileFooter={mobileFooter}
      mobileCloseLabel={mobileCloseLabel}
      className={className}
      controlClassName={controlClassName}
      menuClassName={menuClassName}
    />
  )
}

type FormMemberAsyncSelectFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  control: Control<TFieldValues>
  name: TName
  label?: React.ReactNode
  description?: React.ReactNode
  required?: boolean
  className?: string
  controlClassName?: string
  clearable?: boolean
  targetBranchId?: string | null
  forceParishContext?: boolean
  placeholder?: string
  disabled?: boolean
  staticOptions?: AsyncLookupOption[]
  fallbackOptions?: AsyncLookupOption[]
  selectedLabel?: string
  searchPlaceholder?: string
  loadingMessage?: React.ReactNode
  noOptionsMessage?: React.ReactNode
  mobileEmpty?: React.ReactNode
  mobileTitle?: React.ReactNode
  mobileDescription?: React.ReactNode
  mobileFooter?: React.ReactNode
  mobileCloseLabel?: string
  mapValueToField?: (value: string) => string | undefined
}

export function FormMemberAsyncSelectField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  description,
  required,
  className,
  controlClassName,
  clearable,
  targetBranchId,
  forceParishContext,
  placeholder,
  disabled,
  staticOptions,
  fallbackOptions,
  selectedLabel,
  searchPlaceholder,
  loadingMessage,
  noOptionsMessage,
  mobileEmpty,
  mobileTitle,
  mobileDescription,
  mobileFooter,
  mobileCloseLabel,
  mapValueToField,
}: FormMemberAsyncSelectFieldProps<TFieldValues, TName>) {
  return (
    <FormControlledField
      control={control}
      name={name}
      label={label}
      description={description}
      required={required}
      className={className}
      controlClassName={controlClassName}
      renderInput={({ field }) => {
        const value = typeof field.value === "string" ? field.value : ""

        return (
          <MemberAsyncSelect
            value={value}
            onValueChange={(nextValue) => {
              field.onChange(mapValueToField ? mapValueToField(nextValue) ?? "" : nextValue)
            }}
            targetBranchId={targetBranchId}
            forceParishContext={forceParishContext}
            placeholder={placeholder}
            disabled={disabled}
            clearable={clearable}
            staticOptions={staticOptions}
            fallbackOptions={fallbackOptions}
            selectedLabel={selectedLabel}
            searchPlaceholder={searchPlaceholder}
            loadingMessage={loadingMessage}
            noOptionsMessage={noOptionsMessage}
            mobileEmpty={mobileEmpty}
            mobileTitle={mobileTitle}
            mobileDescription={mobileDescription}
            mobileFooter={mobileFooter}
            mobileCloseLabel={mobileCloseLabel}
          />
        )
      }}
    />
  )
}
